import React, { useState, useEffect } from 'react';
import { Package, AlertCircle, CheckCircle, Clock, ArrowLeft, Zap, Tag, TrendingUp, LogOut, User } from 'lucide-react';
import LoginPage from './components/LoginPage';
import ClientsPage from './components/ClientsPage';
import ClientDashboard from './components/ClientDashboard';
import StagingList from './components/StagingList';
import LiveProducts from './components/LiveProducts';
import BuyBox from './components/BuyBox';
import ErrorsList from './components/ErrorsList';
import StatsCard from './components/StatsCard';
import { syncService, setClientContext, clearClientContext } from './services/api';
import authService from './services/auth';
import './styles/global.css';

const Dashboard = ({ client, marketplace }) => {
  const [stats, setStats] = React.useState({ byStatus: {}, byValidation: {} });
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await syncService.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const totalProducts = stats.total || Object.values(stats.byStatus || {}).reduce((a, b) => a + b, 0);
  const publishedCount = stats.byStatus?.PUBLISHED || 0;
  const failedCount = stats.byStatus?.FAILED || 0;
  const pendingCount = stats.byStatus?.PENDING_APPROVAL || 0;
  const validCount = stats.byValidation?.VALID || 0;
  const invalidCount = stats.byValidation?.INVALID || 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p className="text-secondary" style={{ marginTop: '4px' }}>
            {client?.name} ? Leroy Merlin
          </p>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner" />
        </div>
      ) : (
        <>
          <div style={styles.statsGrid}>
            <StatsCard title="Total in Staging" value={totalProducts} icon={Package} variant="info" />
            <StatsCard title="Valid" value={validCount} icon={CheckCircle} variant="success" />
            <StatsCard title="Invalid" value={invalidCount} icon={AlertCircle} variant="error" />
            <StatsCard title="Pending Approval" value={pendingCount} icon={Clock} variant="warning" />
            <StatsCard title="Published" value={publishedCount} icon={CheckCircle} variant="success" />
            <StatsCard title="Failed" value={failedCount} icon={AlertCircle} variant="error" />
          </div>
        </>
      )}
    </div>
  );
};

const AppSidebar = ({ activeTab, setActiveTab, client, marketplace, onBack, user, onLogout }) => {
  return (
    <aside style={styles.sidebar}>
      <div style={styles.sidebarHeader}>
        <div style={styles.logoBox}>
          <Zap size={24} color="#fff" />
        </div>
        <span style={styles.logoText}>SyncBridge</span>
      </div>

      <button style={styles.backBtn} onClick={onBack}>
        <ArrowLeft size={18} />
        Back to Clients
      </button>

      <div style={styles.clientSection}>
        <div style={styles.clientBox}>
          {client?.logo ? (
            <img src={client.logo} alt="" style={styles.clientLogo} />
          ) : (
            <div style={styles.clientLogoPlaceholder}>
              {client?.name?.charAt(0)}
            </div>
          )}
          <div style={styles.clientDetails}>
            <span style={styles.clientName}>{client?.name}</span>
            <span style={styles.marketplaceBadge}>Leroy Merlin</span>
          </div>
        </div>
      </div>

      <nav style={styles.nav}>
        <button
          style={activeTab === 'dashboard' ? {...styles.navItem, ...styles.navItemActive} : styles.navItem}
          onClick={() => setActiveTab('dashboard')}
        >
          <Package size={20} />
          Dashboard
        </button>
        <button
          style={activeTab === 'staging' ? {...styles.navItem, ...styles.navItemActive} : styles.navItem}
          onClick={() => setActiveTab('staging')}
        >
          <Package size={20} />
          Product Staging
        </button>
        <button
          style={activeTab === 'live' ? {...styles.navItem, ...styles.navItemActive} : styles.navItem}
          onClick={() => setActiveTab('live')}
        >
          <Tag size={20} />
          Live Products
        </button>
        <button
          style={activeTab === 'buybox' ? {...styles.navItem, ...styles.navItemActive} : styles.navItem}
          onClick={() => setActiveTab('buybox')}
        >
          <TrendingUp size={20} />
          Buy Box
        </button>
        <button
          style={activeTab === 'errors' ? {...styles.navItem, ...styles.navItemActive} : styles.navItem}
          onClick={() => setActiveTab('errors')}
        >
          <AlertCircle size={20} />
          Errors
        </button>
      </nav>

      <div style={styles.userSection}>
        <div style={styles.userInfo}>
          <div style={styles.userAvatar}>
            {user?.avatar ? (
              <img src={user.avatar} alt="" style={styles.userAvatarImg} />
            ) : (
              <User size={18} color="#fff" />
            )}
          </div>
          <div style={styles.userDetails}>
            <span style={styles.userName}>{user?.name || user?.email}</span>
            <span style={styles.userRole}>{user?.role?.replace('_', ' ')}</span>
          </div>
        </div>
        <button style={styles.logoutBtn} onClick={onLogout} title="Logout">
          <LogOut size={18} />
        </button>
      </div>
    </aside>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
  const [user, setUser] = useState(authService.getUser());
  const [view, setView] = useState('clients');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMarketplace, setSelectedMarketplace] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    // Handle Google OAuth callback
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('accessToken');
    const refreshToken = params.get('refreshToken');
    const error = params.get('error');

    if (error) {
      alert(error);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (accessToken && refreshToken) {
      authService.handleGoogleCallback(accessToken, refreshToken).then((user) => {
        setUser(user);
        setIsAuthenticated(true);
        window.history.replaceState({}, document.title, window.location.pathname);
      }).catch((err) => {
        console.error('Google callback error:', err);
      });
    }
  }, []);

  const handleLoginSuccess = () => {
    setUser(authService.getUser());
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    setView('clients');
    setSelectedClient(null);
    setSelectedMarketplace(null);
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setView('client-dashboard');
  };

  const handleSelectMarketplace = (client, marketplace) => {
    setSelectedClient(client);
    setSelectedMarketplace(marketplace);
    const wooConnection = client.woocommerceConnections?.[0];
    setClientContext(client, wooConnection, marketplace);
    setView('marketplace');
    setActiveTab('dashboard');
  };

  const handleBackToClients = () => {
    clearClientContext();
    setSelectedClient(null);
    setSelectedMarketplace(null);
    setView('clients');
  };

  const handleBackToClientDashboard = () => {
    clearClientContext();
    setSelectedMarketplace(null);
    setView('client-dashboard');
  };

  const renderMarketplaceContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard client={selectedClient} marketplace={selectedMarketplace} />;
      case 'staging':
        return <StagingList />;
      case 'live':
        return selectedMarketplace ? (
          <LiveProducts
            miraklConnectionId={selectedMarketplace.id}
            connectionName="Leroy Merlin"
          />
        ) : null;
      case 'buybox':
        return selectedMarketplace ? (
          <BuyBox
            miraklConnectionId={selectedMarketplace.id}
          />
        ) : null;
      case 'errors':
        return <ErrorsList />;
      default:
        return <Dashboard client={selectedClient} marketplace={selectedMarketplace} />;
    }
  };

  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  if (view === 'clients') {
    return <ClientsPage onSelectClient={handleSelectClient} user={user} onLogout={handleLogout} />;
  }

  if (view === 'client-dashboard') {
    return (
      <ClientDashboard
        client={selectedClient}
        onBack={handleBackToClients}
        onSelectMarketplace={handleSelectMarketplace}
        user={user}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div style={styles.appContainer}>
      <AppSidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        client={selectedClient}
        marketplace={selectedMarketplace}
        onBack={handleBackToClientDashboard}
        user={user}
        onLogout={handleLogout}
      />
      <main style={styles.mainContent}>
        {renderMarketplaceContent()}
      </main>
    </div>
  );
}

const styles = {
  appContainer: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#f5f5f7',
  },
  sidebar: {
    width: '260px',
    backgroundColor: '#1d1d1f',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '20px',
  },
  logoBox: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoImg: {
    width: '40px',
    height: '40px',
    objectFit: 'contain',
  },
  logoText: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#fff',
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '0 12px 16px 12px',
    padding: '10px 12px',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    border: 'none',
    borderRadius: '8px',
    color: '#a1a1a6',
    fontSize: '13px',
    cursor: 'pointer',
  },
  clientSection: {
    padding: '0 12px 20px 12px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    marginBottom: '12px',
  },
  clientBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '12px',
  },
  clientLogo: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    objectFit: 'cover',
  },
  clientLogoPlaceholder: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    backgroundColor: '#3a3a3c',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
  },
  clientDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  clientName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#fff',
  },
  marketplaceBadge: {
    fontSize: '12px',
    color: '#a1a1a6',
  },
  nav: {
    padding: '0 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 14px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '10px',
    color: '#a1a1a6',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    textAlign: 'left',
  },
  navItemActive: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#fff',
  },
  mainContent: {
    flex: 1,
    marginLeft: '260px',
    padding: '32px 40px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '20px',
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 12px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  userAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#3a3a3c',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  userAvatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  userName: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#fff',
  },
  userRole: {
    fontSize: '11px',
    color: '#8e8e93',
    textTransform: 'capitalize',
  },
  logoutBtn: {
    padding: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '6px',
    color: '#8e8e93',
    cursor: 'pointer',
  },
};

export default App;

